import React from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
// Import CSS - Vite will extract it to a separate file
import './styles.css';

// Global initialization function
(window as any).IntanAddonProduct = {
  init: (config: { apiUrl: string }) => {
    console.log('Init Intan V2 Addon Product');

    // Get API URL from config or data attribute
    const apiUrl =
      config?.apiUrl ||
      document.querySelector('[data-intan-api-url]')?.getAttribute('data-intan-api-url') ||
      (document.querySelector('script[data-intan-api-url]') as HTMLScriptElement)?.dataset.intanApiUrl ||
      '';

    if (!apiUrl) {
      console.error('IntanAddonProduct: API URL is required. Provide it via config or data-intan-api-url attribute.');
      return;
    }

    // Function to extract product and variant IDs from page
    const extractProductInfo = (): { productId: string | null; variantId: string | null } => {
      // Method 1: Check for data attributes (preferred - set by Liquid template)
      const container = document.querySelector('[data-intan-addon-container]');
      if (container) {
        const productId = container.getAttribute('data-intan-product-id');
        const variantId = container.getAttribute('data-intan-variant-id');
        if (productId && variantId) {
          return { productId, variantId };
        }
      }

      // Method 2: Check script tag data attributes
      const script = document.querySelector('script[data-intan-api-url]') as HTMLScriptElement;
      if (script?.dataset.intanProductId && script?.dataset.intanVariantId) {
        return {
          productId: script.dataset.intanProductId,
          variantId: script.dataset.intanVariantId,
        };
      }

      // Method 3: Try to extract from Shopify's global analytics object
      const productData = (window as any).Shopify?.analytics?.meta?.product;
      if (productData) {
        const productId = productData.id?.toString();
        const variantId = productData.selectedVariantId?.toString() || productData.variants?.[0]?.id?.toString();
        if (productId && variantId) {
          return { productId, variantId };
        }
      }

      // Method 4: Product JSON script tag (common in Shopify themes)
      const productJsonSelectors = [
        'script[type="application/json"][data-product]',
        'script#ProductJson',
        'script[type="application/json"][id*="Product"]',
      ];

      for (const selector of productJsonSelectors) {
        const productJson = document.querySelector(selector);
        if (productJson) {
          try {
            const product = JSON.parse(productJson.textContent || '{}');
            if (product.id) {
              const variantId = product.selected_or_first_available_variant?.id?.toString() ||
                product.variants?.[0]?.id?.toString();
              if (variantId) {
                return {
                  productId: product.id.toString(),
                  variantId: variantId.toString(),
                };
              }
            }
          } catch (e) {
            console.error('Failed to parse product JSON:', e);
          }
        }
      }

      // Method 5: Check URL parameters (for variant selection)
      const urlParams = new URLSearchParams(window.location.search);
      const variantParam = urlParams.get('variant');
      if (variantParam) {
        // Try to get product ID from page
        const productId = productData?.id?.toString();
        if (productId) {
          return { productId, variantId: variantParam };
        }
      }

      return { productId: null, variantId: null };
    };

    // Function to check if product has addon tag via data attribute
    const fetchApplicableAddonCount = async (
      productId: string,
      variantId: string
    ): Promise<number> => {
      try {
        const url = `${apiUrl}/product-addons/product/${productId}/variant/${variantId}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) {
          return 0;
        }
        const json = await response.json();
        if (!json?.success || !Array.isArray(json?.data)) {
          return 0;
        }
        return json.data.length;
      } catch (_err) {
        return 0;
      }
    };

    // Function to initialize
    const initialize = () => {
      const { productId, variantId } = extractProductInfo();

      if (!productId || !variantId) {
        console.warn('IntanAddonProduct: Could not extract product ID and variant ID from page.');
        return;
      }

      // Get initial variant ID (from URL or default)
      const getInitialVariantId = (): string => {
        const urlParams = new URLSearchParams(window.location.search);
        const urlVariantId = urlParams.get('variant');
        return urlVariantId || variantId;
      };

      let variantWatcher: number | null = null;
      const mountAddonUI = () => {
        // Find the buy-buttons container or form and hide it (product is qualified for addons)
        const buyButtonsContainer = document.querySelector('buy-buttons.buy-buttons-row') as HTMLElement;
        const formSelectors = [
          'form[action*="/cart/add"]',
          'form.js-product-form',
          '.product-form',
          '[data-product-form]',
          '.product__form',
          '#product-form',
          '.product-single__form',
          '[data-product-form-container]',
        ];

        let referenceElement: HTMLElement | null = null;

        // Prefer buy-buttons container
        if (buyButtonsContainer) {
          buyButtonsContainer.style.display = 'none';
          buyButtonsContainer.setAttribute('data-intan-hidden', 'true');
          referenceElement = buyButtonsContainer;
        } else {
          for (const selector of formSelectors) {
            const found = document.querySelector(selector) as HTMLElement;
            if (found) {
              found.style.display = 'none';
              found.setAttribute('data-intan-hidden', 'true');
              referenceElement = found;
              break;
            }
          }
        }

        // Check if container already exists (created by Liquid template)
        let addonContainer = document.querySelector('#intan-addon-product-container') as HTMLElement;

        if (!addonContainer) {
          // Create container for our component if it doesn't exist
          addonContainer = document.createElement('div');
          addonContainer.id = 'intan-addon-product-container';
          addonContainer.setAttribute('data-intan-addon-container', '');
          addonContainer.setAttribute('data-intan-product-id', productId);
          addonContainer.setAttribute('data-intan-variant-id', getInitialVariantId());
          addonContainer.style.cssText = 'width: 100%; margin: 20px 0;';

          // Insert as sibling right after the reference element (same parent, same level)
          if (referenceElement && referenceElement.parentElement) {
            const parent = referenceElement.parentElement;
            if (referenceElement.nextSibling) {
              parent.insertBefore(addonContainer, referenceElement.nextSibling);
            } else {
              parent.appendChild(addonContainer);
            }
          } else {
            // Fallback: append to body
            console.warn('IntanAddonProduct: Could not find reference element parent. Appending to body.');
            document.body.appendChild(addonContainer);
          }
        }

        // Render React app (only if not already rendered)
        if (!addonContainer.hasAttribute('data-intan-rendered')) {
          addonContainer.setAttribute('data-intan-rendered', 'true');
          const root = createRoot(addonContainer);
          root.render(
            React.createElement(App, {
              apiUrl,
              productId,
              initialVariantId: getInitialVariantId(),
            })
          );
        } else {
          addonContainer.setAttribute('data-intan-variant-id', getInitialVariantId());
        }

        if (variantWatcher) {
          window.clearInterval(variantWatcher);
          variantWatcher = null;
        }
      };

      const maybeMount = async (variantToCheck: string) => {
        // If already mounted, just keep variant attribute up to date
        const existingContainer = document.querySelector('#intan-addon-product-container') as HTMLElement | null;
        if (existingContainer?.hasAttribute('data-intan-rendered')) {
          existingContainer.setAttribute('data-intan-variant-id', variantToCheck);
          return;
        }

        const addonCount = await fetchApplicableAddonCount(productId, variantToCheck);
        if (addonCount > 0) {
          mountAddonUI();
        }
      };

      // Initial check
      void maybeMount(getInitialVariantId());

      // Watch for variant changes. If a different variant becomes eligible, mount then.
      let lastVariantId = getInitialVariantId();
      variantWatcher = window.setInterval(() => {
        const current = getInitialVariantId();
        if (current !== lastVariantId) {
          lastVariantId = current;
          void maybeMount(current);
        }
      }, 800);
    };

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialize);
    } else {
      // DOM already loaded, initialize immediately
      initialize();
    }
  },
};

// Auto-initialize if data attributes are present
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const script = document.querySelector('script[data-intan-api-url]') as HTMLScriptElement;
    if (script?.dataset.intanApiUrl) {
      (window as any).IntanAddonProduct.init({
        apiUrl: script.dataset.intanApiUrl,
      });
    }
  });
} else {
  const script = document.querySelector('script[data-intan-api-url]') as HTMLScriptElement;
  if (script?.dataset.intanApiUrl) {
    (window as any).IntanAddonProduct.init({
      apiUrl: script.dataset.intanApiUrl,
    });
  }
}

